import React, { useState, useEffect } from "react";
import { Sparkles, ArrowRight, X, Briefcase, Users, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * StaffUpgradeGate — modal disparado quando um colaborador (staff vinculado)
 * tenta executar uma ação restrita ao owner (criar evento, etc.).
 *
 * Reage ao custom event "liventy:staff-upgrade-gate" disparado pelo interceptor
 * global axios em /app/frontend/src/App.js sempre que o backend retorna 403
 * com a mensagem contendo "colaborador".
 *
 * UX deliberadamente NÃO bloqueante: o usuário pode fechar e continuar
 * navegando como staff. O CTA leva ao /upgrade para ativar "Meu Espaço Pro".
 */
export default function StaffUpgradeGate() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (ev) => {
      setMessage(ev?.detail?.message || null);
      setOpen(true);
    };
    window.addEventListener("liventy:staff-upgrade-gate", handler);
    return () => window.removeEventListener("liventy:staff-upgrade-gate", handler);
  }, []);

  if (!open) return null;

  const close = () => setOpen(false);
  const goUpgrade = () => {
    setOpen(false);
    navigate("/upgrade");
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center p-4 bg-black/55"
      style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onClick={close}
      data-testid="staff-upgrade-gate-overlay"
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-upgrade-gate-modal"
      >
        <div className="flex items-start justify-between mb-2">
          <div
            className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] font-semibold text-white"
            style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}
          >
            <Briefcase size={11} /> Modo colaborador
          </div>
          <button
            onClick={close}
            className="w-9 h-9 -mr-2 -mt-2 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500"
            data-testid="staff-upgrade-gate-close"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <h3 className="text-[22px] sm:text-[26px] font-semibold tracking-tight text-zinc-900 leading-tight mt-1">
          Você está em modo Colaborador
        </h3>
        <p className="text-[13.5px] text-zinc-500 mt-2 leading-relaxed">
          Sua conta está vinculada a outro organizador. Para criar e gerenciar
          seus <strong className="text-zinc-700">próprios eventos</strong>, ative o
          plano Pro e tenha seu espaço exclusivo dentro da Liventy.
        </p>

        <div className="mt-5 grid grid-cols-1 gap-1.5 text-[12.5px] text-zinc-700">
          {[
            "Espaço exclusivo separado do organizador",
            "Crie seus próprios eventos e clientes",
            "Convide sua equipe (multi-evento)",
            "Mantém acesso aos eventos atuais",
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <Sparkles size={12} className="text-violet-500" />
              <span>{it}</span>
            </div>
          ))}
        </div>

        <button
          onClick={goUpgrade}
          className="w-full mt-6 h-12 rounded-full text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          style={{
            backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
            boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
          }}
          data-testid="staff-upgrade-gate-cta"
        >
          Ativar Meu Espaço Pro
          <ArrowRight size={15} />
        </button>
        <button
          onClick={close}
          className="w-full mt-2 h-10 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900"
          data-testid="staff-upgrade-gate-dismiss"
        >
          Continuar como colaborador
        </button>
      </div>
    </div>
  );
}
